import { Component } from '@theme/component';

/*
  EU harmonised notice on the legal guarantee of conformity, shown in a modal dialog.

  The Commission's practical guidelines (Ares(2026)4331985, section 2.3) show the notice
  appearing in full on the first click of a line such as "Your legal guarantee rights".
  One dialog serves every trigger on the page: buy buttons, quick add, cart page and
  cart drawer. A trigger opens it declaratively:

    <button type="button" on:click="#eu-warranty-notice-dialog/open">…</button>

  Deliberately not built on DialogComponent (assets/dialog.js): that one locks the page
  by fixing the body at the current scroll offset. Opened from inside the cart drawer
  or the quick add modal, which already hold that lock, it would read a scroll offset
  of 0 and release the lock of the dialog behind it on close. The scroll lock here is
  CSS only (assets/eu-warranty-notice.css) and stacks with any other dialog.
*/

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLTemplateElement} [imageTemplate] - Holds the notice until the first open.
 *
 * @extends Component<Refs>
 */
class EuWarrantyNoticeDialog extends Component {
  requiredRefs = ['dialog'];

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {EventTarget | null} */
  #pressTarget = null;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    dialog.addEventListener('pointerdown', this.#handlePointerDown);
    dialog.addEventListener('click', this.#handleClick);
    dialog.addEventListener('close', this.#handleClose);

    this.#openForEarlyClick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;
    dialog.removeEventListener('pointerdown', this.#handlePointerDown);
    dialog.removeEventListener('click', this.#handleClick);
    dialog.removeEventListener('close', this.#handleClose);
  }

  /**
   * Opens the dialog. Called from a trigger's `on:click`, whose proxied event carries
   * the trigger itself as target.
   *
   * @param {Event} [event]
   */
  open(event) {
    this.#show(event?.target instanceof HTMLElement ? event.target : null);
  }

  close() {
    this.refs.dialog.close();
  }

  /** @param {HTMLElement | null} opener - Gets the focus back on close. */
  #show(opener) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#opener = opener;
    this.#renderImage();
    dialog.showModal();
  }

  /*
    A trigger clicked before this module ran reaches no component, so the inline script in
    snippets/eu-warranty-notice-dialog.liquid marks it instead. The notice then opens as
    soon as the element is defined, and the click is not lost.
  */
  #openForEarlyClick() {
    const triggers = document.querySelectorAll('[data-eu-warranty-pending]');

    if (!triggers.length) return;

    for (const trigger of triggers) trigger.removeAttribute('data-eu-warranty-pending');

    const opener = triggers[triggers.length - 1];
    this.#show(opener instanceof HTMLElement && opener.isConnected ? opener : null);
  }

  /*
    The notice ships inside a <template> so its SVG, several hundred kilobytes, is only
    requested once someone asks to read it. `loading="lazy"` is not enough: Chrome
    fetches lazy images inside a closed dialog.
  */
  #renderImage() {
    const { imageTemplate } = this.refs;

    if (imageTemplate) imageTemplate.replaceWith(imageTemplate.content.cloneNode(true));
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#pressTarget = event.target;
  };

  /**
   * The panel fills the dialog box, so a click that targets the dialog itself landed on
   * the backdrop. A native listener on purpose: the declarative `on:click` would report
   * the dialog as target for every click inside it.
   *
   * The press has to start on the backdrop too. A drag that starts in the panel (selecting
   * text, pulling a scrollbar) and ends outside it fires its click on the common
   * ancestor, which is the dialog, and must not close it.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const pressedBackdrop = this.#pressTarget === this.refs.dialog;
    this.#pressTarget = null;

    if (pressedBackdrop && event.target === this.refs.dialog) this.close();
  };

  /*
    Safari does not focus a button on mouse click, so the browser may have nothing to
    restore focus to. Returning it explicitly keeps keyboard and screen reader users in
    place, including when the trigger sits in the cart drawer behind the dialog.
  */
  #handleClose = () => {
    if (this.#opener?.isConnected) this.#opener.focus();
    this.#opener = null;
  };
}

if (!customElements.get('eu-warranty-notice-dialog')) {
  customElements.define('eu-warranty-notice-dialog', EuWarrantyNoticeDialog);
}
